import { useState } from "react";
import { roleType } from "@/constants";
import { isNetworkAvailable } from "@/utils/request";
import { generateUuid } from "@/utils";
import dayjs from "dayjs";

const useChatMessages = () => {
  const [dialogueMessages, setDialogueMessages] = useState<any[]>([]);

  //添加初始化的用户信息数据
  const addUserMessage = (content: string) => {
    const userMessage = {
      id: generateUuid(),
      role: roleType.user,
      message: content,
      create_time: dayjs().unix(),
      needRetry: !isNetworkAvailable,
      retryLoading: false,
    };
    setDialogueMessages((prev) => [...prev, userMessage]);
    return userMessage;
  };

  //添加初始化的智能体消息数据
  const addAssistantMessage = () => {
    const assistantMessage = {
      id: generateUuid(),
      role: roleType.assistant,
      message: "",
      create_time: dayjs().unix(),
      temporary: 1, //输出人工客服后删除temporary为1的消息
      loading: true,
    };
    setDialogueMessages((prev) => [...prev, assistantMessage]);
    return assistantMessage;
  };

  //更新消息
  const updateMessage = (messageId: string, updates: any) => {
    setDialogueMessages((prev) =>
      prev.map((msg) => (msg.id === messageId ? { ...msg, ...updates } : msg))
    );
  };

  //删除消息
  const removeMessage = (messageId: string | undefined) => {
    setDialogueMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  };

  // 新增chat信息
  const addNewChatMessage = (data: any) => {
    const messageList = data?.messages || [];
    setDialogueMessages((prev) => {
      if (prev.length === 0 && messageList.length === 0) return prev;
      const hasTemporary = prev.some(
        (item) =>
          item && Object.hasOwn(item, "temporary") && item?.temporary === 1
      );
      //过滤掉temporary为1的数据
      const filteredMessages = hasTemporary
        ? prev.filter((item) => item?.temporary !== 1)
        : prev;
      // 处理新消息
      let newMessages: any[] = [];

      messageList.forEach((item: any) => {
        // 检查是否有附件
        if (item?.attachments && item.attachments.length > 0) {
          // 检查 message 是否只是文件名（与附件文件名相同）
          const attachmentFileName = item.attachments[0]?.name || "";
          const isMessageOnlyFileName =
            item.message && item.message.trim() === attachmentFileName;

          // 如果 message 只是文件名，则只显示附件消息，不拆分
          if (isMessageOnlyFileName || !item.message) {
            // 只添加附件消息，不创建文本消息
            newMessages.push({
              ...item,
              message: "", // 清空文本内容，因为只是文件名
              loading: false,
            });
          } else {
            // 如果 message 有其他内容，则创建两条消息
            // 1. 附件消息（只包含附件，文本内容为空）
            const attachmentMessage = {
              ...item,
              message: "", // 清空文本内容
              loading: false,
            };

            // 2. 文本消息（只包含文本内容，不包含附件）
            const textMessage = {
              ...item,
              attachments: [], // 清空附件
              loading: false,
            };

            // 优先添加文本消息，再添加附件消息
            newMessages.push(textMessage, attachmentMessage);
          }
        } else {
          // 如果没有附件，按原来的方式处理
          newMessages.push({
            ...item,
            loading: false,
          });
        }
      });

      // 过滤掉重复的消息
      newMessages = newMessages.filter(
        (newMsg: any) =>
          !filteredMessages.some(
            (existingMsg: any) => existingMsg?.id === newMsg?.id
          )
      );

      return [...filteredMessages, ...newMessages];
    });
  };

  return {
    dialogueMessages,
    setDialogueMessages,
    addUserMessage,
    addAssistantMessage,
    updateMessage,
    removeMessage,
    generateUuid,
    addNewChatMessage,
  };
};

export default useChatMessages;
